import { MessageSquare, Trash2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useSessions, useDeleteSession } from '@/hooks/useApi';
import { getErrorMessage } from '@/lib/errors';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { SessionSearch } from '@/components/SessionSearch';
import { SessionSort, type SortOption, type SortDirection } from '@/components/SessionSort';
import { useAppStore } from '@/stores/appStore';
import type { Session } from '@/types';

// Helper function to get session title
function getSessionTitle(session: Session): string {
  // Use the title field if available
  if (session.title) {
    return session.title;
  }

  // Fallback: Try to get a meaningful title from the first user message
  const firstUserEvent = session.events?.find(
    (event) => event.content?.role === 'user'
  );

  if (firstUserEvent?.content?.parts?.[0]?.text) {
    const text = firstUserEvent.content.parts[0].text;
    return text.length > 30 ? `${text.slice(0, 30)}...` : text;
  }

  return `Session ${session.id.slice(0, 8)}`;
}

interface SessionItemProps {
  session: Session;
  isActive: boolean;
  onSelect: (sessionId: string) => void;
  onDelete: (sessionId: string) => void;
  isDeleting: boolean;
  currentSessionId?: string;
}

function SessionItem({ session, isActive, onSelect, onDelete, isDeleting, currentSessionId }: SessionItemProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeletingSession, setIsDeletingSession] = useState(false);

  const formatDate = (dateInput: string | number) => {
    try {
      let date: Date;

      if (typeof dateInput === 'string') {
        // Handle ISO datetime string
        date = new Date(dateInput);
      } else {
        // Handle Unix timestamp (legacy support)
        date = new Date(dateInput * 1000);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }

      // Format as "MMM dd, yyyy" (e.g., "Sep 19, 2025")
      return date.toLocaleDateString('en-US', {
        month: 'short',    // MMM (Sep, Oct, Nov, etc.)
        day: '2-digit',    // dd (01, 02, 19, etc.)
        year: 'numeric'    // yyyy (2025)
      });
    } catch (error) {
      console.error('Error formatting date:', dateInput, error);
      return 'Invalid date';
    }
  };

  return (
    <Card
      className={`cursor-pointer transition-colors hover:bg-muted/50 ${isActive ? 'ring-2 ring-primary bg-muted/30' : ''
        }`}
      onClick={() => {
        if (!isDeletingSession && !deleteDialogOpen) {
          onSelect(session.id);
        }
      }}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 mb-1">
              <MessageSquare className="h-3 w-3 text-muted-foreground flex-shrink-0 mt-0.5" />
              <h4 className="text-sm font-medium break-words leading-tight" title={getSessionTitle(session)}>
                {getSessionTitle(session)}
              </h4>
            </div>

            <div className="text-xs text-muted-foreground ml-5">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 flex-shrink-0" />
                <span className="break-words">
                  {session.lastUpdateTime ? formatDate(session.lastUpdateTime) : 'No date'}
                </span>
              </div>
            </div>

            {/* Session status indicator */}
            <div className="mt-1 ml-5">
              <div className="flex items-center gap-1 text-xs">
                <div className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${session.id === currentSessionId ? 'bg-green-500' : 'bg-muted-foreground/50'
                  }`} />
                <span className="text-muted-foreground">
                  {session.id === currentSessionId ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 ml-2">

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                  onClick={(e) => e.stopPropagation()}
                  disabled={isDeleting}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Session</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this session? This action cannot be undone.
                    All messages and artifacts in this session will be permanently removed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDeletingSession(true);
                      onDelete(session.id);
                      setDeleteDialogOpen(false);
                      // Reset the flag after a short delay to allow for async operations
                      setTimeout(() => setIsDeletingSession(false), 1000);
                    }}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete Session
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function SessionList() {
  const { data: sessions, isLoading, error } = useSessions();
  const deleteSessionMutation = useDeleteSession();
  const navigate = useNavigate();
  const { sessionId: currentSessionId } = useParams();
  const { setCurrentSession, setMessages, setWelcomeState } = useAppStore();

  // Search and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('updated');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Filter and sort sessions
  const filteredAndSortedSessions = useMemo(() => {
    if (!sessions) return [];

    // Filter sessions based on search query
    const filtered = sessions.filter((session) => {
      if (!searchQuery) return true;

      const query = searchQuery.toLowerCase();
      const sessionTitle = getSessionTitle(session).toLowerCase();
      const sessionId = session.id.toLowerCase();

      return sessionTitle.includes(query) || sessionId.includes(query);
    });

    // Sort sessions
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'updated':
          // Compare ISO datetime strings (they sort lexicographically)
          const aUpdated = a.lastUpdateTime || '';
          const bUpdated = b.lastUpdateTime || '';
          comparison = aUpdated.localeCompare(bUpdated);
          break;
        case 'created':
          // Use createdAt if available, otherwise fall back to lastUpdateTime
          const aCreated = a.createdAt || a.lastUpdateTime || '';
          const bCreated = b.createdAt || b.lastUpdateTime || '';
          comparison = aCreated.localeCompare(bCreated);
          break;
        case 'name':
          comparison = getSessionTitle(a).localeCompare(getSessionTitle(b));
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [sessions, searchQuery, sortBy, sortDirection]);

  const handleSelectSession = (sessionId: string) => {
    navigate(`/chat/${sessionId}`);
  };

  const handleDeleteSession = (sessionId: string) => {
    deleteSessionMutation.mutate(sessionId, {
      onSuccess: () => {
        // If we deleted the current session, clear state and enter welcome state
        if (sessionId === currentSessionId) {
          // Clear current session and messages
          setCurrentSession(null);
          setMessages([]);
          // Set welcome state
          setWelcomeState(true);
          // Navigate to chat root (welcome state)
          navigate('/chat', { replace: true });
        }
        // If we're already in welcome state, just ensure the state is clean
        else if (!currentSessionId) {
          // Clear any session state and ensure welcome state
          setCurrentSession(null);
          setMessages([]);
          setWelcomeState(true);
          // Don't navigate - we're already on the welcome page
        }
      },
    });
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleSort = (newSortBy: SortOption, direction: SortDirection) => {
    setSortBy(newSortBy);
    setSortDirection(direction);
  };

  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-3">
              <div className="space-y-2">
                <div className="h-4 bg-muted animate-pulse rounded" />
                <div className="h-3 bg-muted animate-pulse rounded w-2/3" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              {getErrorMessage(error)}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <div className="p-4 text-center">
        <Card>
          <CardContent className="p-4">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No sessions yet. Start a new chat!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Search and Sort Controls */}
      <div className="px-2 space-y-2">
        <SessionSearch onSearch={handleSearch} />
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">
            {filteredAndSortedSessions.length} session{filteredAndSortedSessions.length !== 1 ? 's' : ''}
          </span>
          <SessionSort
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        </div>
      </div>

      {/* Session List */}
      <div className="space-y-2 px-2">
        {filteredAndSortedSessions.length === 0 ? (
          <Card>
            <CardContent className="p-4 text-center">
              <MessageSquare className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {searchQuery ? 'No sessions match your search.' : 'No sessions found.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredAndSortedSessions.map((session) => (
            <div key={session.id} className="group">
              <SessionItem
                session={session}
                isActive={session.id === currentSessionId}
                onSelect={handleSelectSession}
                onDelete={handleDeleteSession}
                isDeleting={deleteSessionMutation.isPending}
                currentSessionId={currentSessionId}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}